import { useState } from "react";
import { toast } from "sonner";
import { Check, Ban, Undo2, Loader2, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SpeciesSearchBox } from "./SpeciesSearchBox";
import { submitReview, undoReview, fileUrl, pct, errMsg } from "../lib/api";

const VERDICT_LABEL = { confirm_top1: "confirmed AI top-1", choose_candidate: "picked another AI candidate", other_species: "different species", unidentifiable: "unidentifiable" };

export const ReviewCard = ({ item, reviewer, onDone, onOpen, idx }) => {
  const [pick, setPick] = useState(0);
  const [other, setOther] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rv = item.review;
  const OTHER = -1;

  const send = async (verdict) => {
    setBusy(true);
    try {
      const species = verdict === "other_species" ? other : verdict === "choose_candidate" ? item.predictions[pick].scientific_name : null;
      await submitReview(item.id, { verdict, species, reviewer, note });
      toast.success(verdict === "unidentifiable" ? "Marked unidentifiable (excluded from evaluation)" : "Label saved, now part of the evaluation set");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = () => (pick === OTHER ? send("other_species") : send(pick === 0 ? "confirm_top1" : "choose_candidate"));
  const undo = async () => { setBusy(true); try { await undoReview(item.id); toast.success("Review undone, back in the queue"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };

  return (
    <div data-testid={`review-card-${idx}`} className="grid md:grid-cols-[180px_1fr] gap-4 border rounded p-3 bg-card">
      <div>
        <img src={fileUrl(item.image_path)} alt="specimen" className="w-full aspect-square object-cover rounded border" />
        <button data-testid={`review-open-${idx}`} onClick={() => onOpen(item.id)} className="mt-1 text-[11px] font-mono text-primary inline-flex items-center gap-1 hover:underline">open record <ExternalLink className="h-3 w-3" /></button>
        <div className="text-[10.5px] font-mono text-muted-foreground">{new Date(item.created_at).toLocaleString()}</div>
      </div>
      <div className="min-w-0 space-y-2">
        <div className="font-mono text-[11.5px] rounded px-2 py-1 bg-[hsl(var(--nb-err-bg))] text-[hsl(var(--nb-err))]">
          ABSTAIN · H_norm={item.normalized_entropy?.toFixed(3)} · agreement={item.ensemble_agreement} · {(item.uncertainty_reasons || []).join("; ")}
        </div>
        {!rv && item.true_label && <div data-testid={`review-existing-label-${idx}`} className="font-mono text-xs text-muted-foreground">existing user label: <i>{item.true_label}</i> (an expert verdict will replace it)</div>}
        {rv ? (
          <div data-testid={`review-done-${idx}`} className="space-y-2">
            <div className="font-mono text-sm">verdict: <b>{VERDICT_LABEL[rv.verdict]}</b>{rv.species && <> → <i>{rv.species}</i></>}</div>
            <div className="font-mono text-xs text-muted-foreground">AI top-1 <i>{rv.model_top1}</i> was {rv.species ? (rv.model_correct ? "correct" : "wrong") : "n/a"} · by {rv.reviewer} · {new Date(rv.reviewed_at).toLocaleString()}{rv.note && ` · "${rv.note}"`}</div>
            <Button data-testid={`review-undo-${idx}`} size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={undo}><Undo2 className="h-3.5 w-3.5 mr-1" />Undo review</Button>
          </div>
        ) : (
          <>
            <div className="space-y-1" role="radiogroup">
              {item.predictions.slice(0, 5).map((p, i) => (
                <label key={p.scientific_name + i} className={`flex items-center gap-2 text-sm rounded px-2 py-1 cursor-pointer ${pick === i ? "bg-accent" : "hover:bg-secondary"}`}>
                  <input data-testid={`review-candidate-${idx}-${i}`} type="radio" checked={pick === i} onChange={() => setPick(i)} className="accent-[hsl(var(--primary))]" />
                  <span className="italic">{p.scientific_name}</span><span className="text-xs text-muted-foreground truncate">{p.common_name}</span>
                  <span className="ml-auto font-mono text-xs">{pct(p.probability)}</span>
                </label>
              ))}
              <label className={`flex items-start gap-2 text-sm rounded px-2 py-1 ${pick === OTHER ? "bg-accent" : ""}`}>
                <input data-testid={`review-candidate-${idx}-other`} type="radio" checked={pick === OTHER} onChange={() => setPick(OTHER)} className="mt-2.5 accent-[hsl(var(--primary))]" />
                <div className="flex-1 min-w-0" onFocus={() => setPick(OTHER)}>
                  <SpeciesSearchBox onSearch={(n) => { setOther(n); setPick(OTHER); }} initial={other} />
                  {other && <div className="text-xs font-mono mt-1">other = <i>{other}</i></div>}
                </div>
              </label>
            </div>
            <Input data-testid={`review-note-${idx}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional): diagnostic features seen…" className="h-8 text-sm" />
            <div className="flex flex-wrap gap-2">
              <Button data-testid={`review-confirm-${idx}`} size="sm" disabled={busy || (pick === OTHER && other.length < 3)} onClick={confirm}>
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Confirm label
              </Button>
              <Button data-testid={`review-unidentifiable-${idx}`} size="sm" variant="outline" disabled={busy} onClick={() => send("unidentifiable")}><Ban className="h-4 w-4 mr-1" />Unidentifiable</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
